import React, { type ReactNode, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider
} from 'react-router-dom'
import DashboardLayout from './layouts/Dashboard'
import AuthLayout from './layouts/Auth'
import LoginPage from './pages/LoginPage'
import RestorePage from './pages/RestorePage'
import RestoreFormPage from './pages/RestoreFormPage'
import SignUpPage from './pages/SignUpPage'
import { AuthStore } from './store/authStore'
import EmailVerificationPage from './pages/EmailVerificationPage'
import ProfilePage from './pages/ProfilePage'
import { type CloseButtonProps, toast, ToastContainer } from 'react-toastify'
import ProfileEditPage from './pages/ProfileEditPage'
import ProfileEditMainPage from './pages/ProfileEditMainPage'
import ProfileEditPasswordChangePage from './pages/ProfileEditPasswordChangePage'
import ProfileEditEducationPage from './pages/ProfileEditEducationPage'
import ProfileEditFeaturesPage from './pages/ProfileEditFeaturesPage'
import ProfileEditJobPage from './pages/ProfileEditJobPage'
import ProfileEditLanguagePage from './pages/ProfileEditLanguagePage'
import ProfileEditAboutOrganizationPage from './pages/ProfileEditAboutOrganizationPage'
import ArticlePage from './pages/ArticlePage'
import { ReactSVG } from 'react-svg'
import CommunitiesPage from './pages/CommunitiesPage'
import RatingPage from './pages/RatingPage'
import MainPage from './pages/MainPage'
import EventsPage from './pages/EventsPage'
import ArticleBasePage from './pages/ArticleBasePage'
import RewardsPage from './pages/RewardsPage'
import PrizesPage from './pages/PrizesPage'
// import PortfolioPage from './pages/PortfolioPage'
import NotFound from './components/NotFound'
import NotificationsPage from './pages/NotificationsPage'
import queryString from 'query-string'
import { getAuthRedirectUrl, getValidatedReturnUrl } from './utils/authRedirect'
import EventsNewPage from './pages/EventsNewPage'
import EventsEditPage from './pages/EventsEditPage'
import PartnersPage from './pages/PartnersPage'
import NewsNewPage from './pages/NewsNewPage'
import ParticipantPage from './pages/ParticipantPage'
import QuestsPage from './pages/QuestsPage'
import { QuestPage } from './pages/QuestPage'
import ParticipantsPage from './pages/ParticipantsPage'

const App = observer(() => {
  useEffect(() => {
    const search = queryString.parse(location.search)

    if (search.successMessage) {
      toast.success(search.successMessage, { autoClose: 10000 })
    }

    if (search.errorMessage) {
      toast.error(search.errorMessage, { autoClose: 10000 })
    }
  }, [])

  const CloseButton = ({ closeToast }: CloseButtonProps): ReactNode => (
    <button
      className="Toastify__close-button Toastify__close-button--light"
      onClick={closeToast}
      type="button"
      aria-label="close"
    >
      <ReactSVG src={'/i/svg/x.svg'} wrapper="span" />
    </button>
  )

  const Dashboard = observer(() => {
    if (AuthStore.isUserAuthenticated) {
      return <DashboardLayout />
    } else {
      return <Navigate to={getAuthRedirectUrl()} />
    }
  })

  const Auth = observer(() => {
    if (AuthStore.isUserAuthenticated) {
      return <Navigate to={getValidatedReturnUrl(location.search)} />
    } else {
      return <AuthLayout />
    }
  })

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<Dashboard />}>
          <Route path="/" element={<MainPage />}></Route>
          <Route path="/communities" element={<CommunitiesPage />}></Route>
          <Route path="/events" element={<EventsPage />}></Route>
          <Route path="/partners" element={<PartnersPage />}></Route>
          <Route path="/events/new" element={<EventsNewPage />}></Route>
          <Route path="/events/:id/edit" element={<EventsEditPage />}></Route>
          <Route
            path="/events/:id/participants"
            element={<ParticipantsPage />}
          ></Route>
          <Route path="/news/new" element={<NewsNewPage />}></Route>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<ProfileEditPage />}>
            <Route path="/profile/edit" element={<ProfileEditMainPage />} />
            <Route
              path="/profile/edit/education"
              element={<ProfileEditEducationPage />}
            />
            <Route
              path="/profile/edit/about-organization"
              element={<ProfileEditAboutOrganizationPage />}
            />
            <Route path="/profile/edit/job" element={<ProfileEditJobPage />} />
            <Route
              path="/profile/edit/language"
              element={<ProfileEditLanguagePage />}
            />
            <Route
              path="/profile/edit/features"
              element={<ProfileEditFeaturesPage />}
            />
            <Route
              path="/profile/edit/password-change"
              element={<ProfileEditPasswordChangePage />}
            />
          </Route>
          <Route
            path="/communities/:id"
            element={<ArticlePage type="community" />}
          />
          <Route path="/events/:id" element={<ArticlePage type="event" />} />
          <Route path="/participant/:id" element={<ParticipantPage />} />
          <Route path="/rating" element={<RatingPage />} />
          <Route path="/tasks/:id" element={<ArticleBasePage type="task" />} />
          <Route
            path="/articles/:id"
            element={<ArticleBasePage type="article" />}
          />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/rewards/prizes" element={<PrizesPage />} />
          {/* <Route path="/portfolio" element={<PortfolioPage />} /> */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/quests" element={<QuestsPage />} />
          <Route path="/quests/:id" element={<QuestPage />} />
        </Route>
        <Route path="/auth" element={<Auth />}>
          <Route path="/auth" element={<LoginPage />} />
          <Route path="/auth/restore" element={<RestorePage />} />
          <Route path="/auth/restore-form" element={<RestoreFormPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
          <Route
            path="/auth/email-verification"
            element={<EmailVerificationPage />}
          />
        </Route>

        <Route
          path="*"
          element={
            <div className={'container'}>
              <NotFound></NotFound>
            </div>
          }
        ></Route>
      </>
    ),
    { basename: process.env.REACT_APP_BASE_PATH || '/' }
  )

  return (
    <>
      <RouterProvider router={router}></RouterProvider>
      <ToastContainer
        autoClose={5000}
        closeOnClick
        draggable={false}
        position={'bottom-right'}
        hideProgressBar={true}
        icon={({ type }) => {
          if (type === 'success') {
            return <ReactSVG src={'/i/svg/check-circle.svg'} wrapper="span" />
          }
          if (type === 'error') {
            return <ReactSVG src={'/i/svg/alert-circle.svg'} wrapper="span" />
          }
        }}
        closeButton={CloseButton}
      />
    </>
  )
})

export default App
